import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "typedefs";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";

import PublicWrapper from "app/components/PublicWrapper";
import SimpleModal from "../SimpleModal";
import { Loading } from "app/components/Wrappers";
import { fetchSelf } from "app/actions/user";
import { useStoreActions } from "app/utils/hooks";
import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { syncAthleteContact } from "app/utils/syncContact";

const Confirmation = () => {
  const [openAuthenticationModel, setOpenAuthenticationModel] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isCreatingPurchase, setIsCreatingPurchase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const purchaseCreatedRef = useRef(false);

  const dispatch = useDispatch();
  const { user } = useStoreActions({ user: fetchSelf });
  const bookSession = useSelector((state: RootState) => state.bookSession);
  const external_id = useSelector(
    (state: RootState): string => state.bookSession.coach?.externalId,
  );

  const { t } = useTranslation();
  const history = useHistory();

  const syncAndRefreshUser = async () => {
    const self = user.self;
    if (!self || !external_id) {
      return;
    }
    await syncAthleteContact(self, external_id);

    await (dispatch as any)(fetchSelf());
  };

  // Create purchase and redirect to checkout
  const createPurchaseAndRedirect = async () => {
    if (purchaseCreatedRef.current || isCreatingPurchase) {
      return;
    }

    purchaseCreatedRef.current = true;
    setIsCreatingPurchase(true);
    setError(null);

    try {
      await syncAndRefreshUser();

      const payload = {
        start: DateTime.fromMillis(bookSession.time.startHour)
          .setZone(bookSession.coach.timeZone)
          .toISO(),
        end: DateTime.fromMillis(bookSession.time.endHour)
          .setZone(bookSession.coach.timeZone)
          .toISO(),
        coachProfileId: bookSession.coach.id,
        lessonRateId: bookSession.pricingValue?.id,
        isPublic: true,
        source: "public_calendar",
      };

      const data = await schedulingFetchPost(
        "/lesson_schedules/stripe_purchases",
        payload,
      );
      history.push(`/purchase/${data.id}`);
    } catch (err) {
      console.error("Error creating purchase:", err);
      setError("Unable to create booking. Please try again.");
      purchaseCreatedRef.current = false;
      setIsCreatingPurchase(false);
    }
  };

  useEffect(() => {
    if (purchaseCreatedRef.current) {
      return;
    }

    if (user.pending === true) {
      setIsCheckingAuth(true);
      return;
    }

    setIsCheckingAuth(false);

    if (user.self === null) {
      // Not logged in - show auth modal
      setOpenAuthenticationModel(true);
    } else if (user.self.kind !== "athlete" && user.self.kind !== "other") {
      // Wrong user type - redirect home
      history.push("/");
    } else {
      // User is authenticated and is athlete/other - create purchase
      setOpenAuthenticationModel(false);
      createPurchaseAndRedirect();
    }
  }, [user]);

  if (isCheckingAuth) {
    return <Loading isLoading loadType="spinner" />;
  }

  if (isCreatingPurchase) {
    return (
      <PublicWrapper>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Loading isLoading loadType="spinner" />
          <p style={{ color: "#6b7280", marginTop: "16px" }}>
            {t("Preparing your booking...")}
          </p>
        </div>
      </PublicWrapper>
    );
  }

  if (error) {
    return (
      <PublicWrapper>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "#dc2626", marginBottom: "16px" }}>{error}</p>
          <button
            onClick={() => {
              purchaseCreatedRef.current = false;
              createPurchaseAndRedirect();
            }}
            style={{
              padding: "10px 24px",
              background: "#039be5",
              color: "#fff",
              border: "none",
              borderRadius: "7px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t("Try Again")}
          </button>
        </div>
      </PublicWrapper>
    );
  }

  return (
    <PublicWrapper>
      <SimpleModal
        isOpen={openAuthenticationModel}
        title={t("Authentication required")}
        buttonText={t("Sign In / Sign Up")}
        onConfirm={() =>
          ((window as any).location =
            `/signin?return_url=${encodeURIComponent(`/public/confirmation?coach_id=${external_id}&phoneRequired=true`)}`)
        }
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            color: "#8C8C8C",
          }}
        >
          <div>{t("Please sign in or sign up to complete your booking.")}</div>
        </div>
      </SimpleModal>
    </PublicWrapper>
  );
};

export default Confirmation;
